import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, map } from 'rxjs';

export interface BlogPost {
  id: number;
  title: string;
  author: string;
  source: string;
  published_at: string;
  resume: string;
  tags: { title: string }[];
  has_document: boolean;
  imageUrl: string;

  description: string;
  date: Date;
  category: string;
  comments: number;
  link: string;
}

@Injectable({
  providedIn: 'root'
})
export class BlogService {
  private apiUrl = environment.apiUrl;
  private httpClient = inject(HttpClient);

  // getPaginatedPosts(page: number, pageSize: number = 6) {
  //   const startIndex = (page - 1) * pageSize;
  //   const endIndex = startIndex + pageSize;
  //   const posts = this.mockPosts.slice(startIndex, endIndex);
  //   const totalPages = Math.ceil(this.mockPosts.length / pageSize);

  //   return {
  //     posts,
  //     totalPages,
  //     currentPage: page,
  //     totalPosts: this.mockPosts.length
  //   };
  // }

  async getPaginatedPostsFromApi(page: number, pageSize: number = 6 , query: string = '') {
    return firstValueFrom(
      this.httpClient.get(`${this.apiUrl}/news?paginate=enabled&per_page=${pageSize}&page=${page}&search=${query}&order_by=published_at&order=desc`)
      .pipe(
        map((response: any) => {
          const posts = response.data;
          const totalPages = response.meta.last_page;
          const currentPage = response.meta.current_page;
          const totalPosts = response.meta.total;

          return {
              posts: posts.map((post: any, index: number) => ({
                id: post.id,
                title: post.title,
                description: post.resume,
                imageUrl: post.image.url,
                date: post.published_at,
                category: post.source,
                comments: 0,
                link: `/news/${index + 1 + (currentPage - 1) * pageSize}/detail/${post.id}`,
                tags: post.tags,
              })),
              totalPages,
              currentPage,
              totalPosts
          };
        })
    ));
  }

  async getRecentPostsFromApi() {
    return firstValueFrom(
      this.httpClient.get(`${this.apiUrl}/news?paginate=enabled&per_page=3&page=1&order_by=published_at&order=desc`)
      .pipe(
        map((response: any) => {
          const posts = response.data;
          const totalPages = response.meta.last_page;
          const currentPage = response.meta.current_page;
          const totalPosts = response.meta.total;

          return {
              posts: posts.map((post: any) => ({
                id: post.id,
                title: post.title,
                description: post.resume,
                imageUrl: post.image.url,
                date: post.published_at,
                category: post.source,
                comments: 0,
                tags: post.tags,
                source: post.source,
                link: `/news/${post.id}`
              })),
              totalPages,
              currentPage,
              totalPosts
          };
        })
    ));
  }

  async getNewsDetailFromApi(id: string) {
    return firstValueFrom(
      this.httpClient.get(`${this.apiUrl}/news/${id}`)
      .pipe(
        map((response: any) => {
          const post = response.data;
          return {
            id: post.id,
            title: post.title,
            description: post.resume,
            content: post.content_html?.content || '',
            imageUrl: post.image.url,
            date: post.published_at,
            category: post.source,
            author: post.author,
            tags: post.tags,
            comments: 0,
            link: `/news/${post.id}`,
            document: post.document,
          };
        })
    ));
  }

  async getPrevAndNextPostFromApi(index: number) {
    const prevIndex = index - 1;
    const nextIndex = index + 1;

    let prevPost = null;
    let nextPost = null;

    if (prevIndex >= 1) {
      prevPost = await this.getPaginatedPostsFromApi(
        prevIndex,
        1,
      );
    }
    if (nextIndex >= 1) {
        nextPost = await this.getPaginatedPostsFromApi(
        nextIndex,
        1,
      );
    }

    return {
      prevPost: {
        id: prevPost?.posts[0]?.id || null,
        index: prevIndex,
      },
      nextPost: {
        id: nextPost?.posts[0]?.id || null,
        index: nextIndex,
      }
    };
  }


  async getNewsTagsFromApi() {
    return firstValueFrom(
      this.httpClient.get(`${this.apiUrl}/news/tags?order_by=title&order=asc&paginate=disabled`)
      .pipe(
        map((response: any) => {
          const tags = response.data.map((tag: any) =>({ title: tag.title }));
          return tags;
        })
    ));
  }

  async shareNewsApi(id: string) {
    return firstValueFrom(
      this.httpClient.patch(`${this.apiUrl}/news/${id}/shared`, {})
      .pipe(
        map((response: any) => {
          return response.data;
        })
    ));
  }
}
